import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Avatar,
    Box,
    Divider,
    Drawer,
    IconButton,
    Toolbar,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import Logout from "../../../../assets/svg-icon/logout.svg";
import {
    allMenuList,
    InstallationMenuList,
    InstallationMenuListLimited,
    MENU_ID_LIST,
    WithoutInstallationMenu,
    buildPath,
    pathMatchesPattern,
} from '../../sidebar-helper/menu-id-list';
import { logoutUser, switchParentUser } from "../../../../slice/UserSlice";
import { formatSidebarAddressLine, getCompanyType } from '../../../../helper/helper';
import useUser from '../../../../custom-hook/use-user';
import { buildCompanyPath, lastInstallationStorageKey, parseContextFromPath } from '../../../../helper/url-builder';

import HeaderBox from './component/header-box';
import DropDownMenuComponent from './component/drop-down-menu-component';
import MenuButton from './component/menu-button';

const DRAWER_WIDTH = 250;
const INSTALLATION_ROLE_ARRAY = ["A", "S", "I", "C", "V"];

function LogoutButton() {
    const dispatch = useDispatch();

    function handleLogout() {
        dispatch(logoutUser());
    }

    return (
        <Box
            paddingTop="2px"
            paddingBottom="2px"
            paddingLeft="16px"
            borderRadius="5px"
            sx={{
                width: "100%",
                textAlign: "left",
                minHeight: "37px",
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
            }}
            onClick={handleLogout}
        >
            <Avatar
                variant="square"
                src={Logout}
                sx={{
                    width: 20,
                    height: 20,
                    bgcolor: "transparent",
                    fill: "white"
                }}
            />
            <Typography
                sx={{ marginLeft: "5px" }}
                color="text.disabled"
                variant="caption"
            >
                Logout
            </Typography>
        </Box>
    );
}

function MenuSidebar() {
    const { getActiveUser, getCompanyId, getUserInstallationId } = useUser();
    const role = useSelector(state => state.authUser.role);
    const authUser = useSelector(state => state.authUser);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const urlParams = useParams();

    useEffect(() => {
        const fromRoute = parseContextFromPath(pathname || '');
        const cId =
            urlParams.companyId?.toString() ||
            fromRoute.companyId ||
            '';
        const instId =
            urlParams.installationId?.toString() ||
            fromRoute.installationId ||
            '';
        const key = lastInstallationStorageKey(cId);
        if (key && instId && instId !== '0') {
            sessionStorage.setItem(key, instId);
        }
    }, [pathname, urlParams.companyId, urlParams.installationId]);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [mobileOpen, setMobileOpen] = useState(false);

    const pathCompanyContext = useMemo(() => parseContextFromPath(pathname || ''), [pathname]);

    // Get IDs from URL params first, fall back to user context
    const companyId =
        urlParams.companyId?.toString() ||
        pathCompanyContext.companyId ||
        getCompanyId()?.toString() ||
        '';
    const installationId =
        urlParams.installationId?.toString() ||
        pathCompanyContext.installationId ||
        getUserInstallationId()?.toString() ||
        '';
    const ActiveUser = getActiveUser();
    const sidebarAddressLine = formatSidebarAddressLine(ActiveUser);

    // Resolve pattern placeholders to actual IDs
    const resolveMenuPath = useCallback((pattern) => {
        return buildPath(pattern, companyId, installationId);
    }, [companyId, installationId]);

    // Deep clone menu items and resolve paths
    const resolveMenuPaths = useCallback((menuItems) => {
        return menuItems.map(item => {
            const resolvedItem = { ...item };
            if (item.path) {
                resolvedItem.path = resolveMenuPath(item.path);
            }
            if (item.child) {
                resolvedItem.child = item.child.map(child => ({
                    ...child,
                    path: child.path ? resolveMenuPath(child.path) : child.path
                }));
            }
            return resolvedItem;
        });
    }, [resolveMenuPath]);

    // Build the drawer menu list based on user role and company type
    const DrawerMenuList = useMemo(() => {
        const companyTypeKey =
            typeof ActiveUser?.companyType === 'object' && ActiveUser?.companyType !== null
                ? (ActiveUser.companyType.companyId || '')
                : (ActiveUser?.companyType || '');
        const baseMenuList = allMenuList[companyTypeKey] || [];
        let menuList = resolveMenuPaths(baseMenuList);

        if (INSTALLATION_ROLE_ARRAY.includes(role)) {
            // Show installation menu if Redux knows about it OR if URL has an installation ID
            // (handles direct navigation to installation URLs)
            if (ActiveUser?.installtionExist || ActiveUser?.installation?.id || installationId) {
                const isZeroInstallation = String(installationId) === "0" || String(ActiveUser?.installation?.id) === "0";
                const menuToUse = isZeroInstallation ? InstallationMenuListLimited : InstallationMenuList;
                menuList = [...menuList, ...resolveMenuPaths([menuToUse])];
            } else {
                const resolvedWithoutInstallation = {
                    ...WithoutInstallationMenu,
                    path: buildCompanyPath.newInstallation(companyId)
                };
                menuList = [...menuList, resolvedWithoutInstallation];
            }
        }

        return menuList;
    }, [role, ActiveUser, companyId, installationId, resolveMenuPaths]);

    // Use pattern matching for menu selection (handles parameterized URLs)
    const selectedMenuFromPath = useMemo(() => {
        return Object.keys(MENU_ID_LIST).filter((menuId) => {
            const patterns = MENU_ID_LIST[menuId];
            return patterns.some(pattern => pathMatchesPattern(pathname, pattern));
        });
    }, [pathname]);

    const [selectedMenu, setSelectedMenu] = useState([...selectedMenuFromPath]);

    useEffect(() => {
        setSelectedMenu([...selectedMenuFromPath]);
    }, [selectedMenuFromPath]);

    const navigateUser = useCallback((path, parentId, childId = "") => {
        setSelectedMenu([parentId, childId]);
        if (isMobile) setMobileOpen(false);
        navigate(path);
    }, [navigate, isMobile]);

    function switchToMainUser() {
        if (authUser.users.length > 1) {
            const parentCompanyId = authUser.users[1]?.companyId?.toString() || '';
            dispatch(switchParentUser([{}]));
            if (parentCompanyId) {
                navigate(buildCompanyPath.about(parentCompanyId));
            }
        }
    }

    const drawerContent = (
        <>
            <Toolbar sx={{ background: "#F5F6FA" }} />
            <Box sx={{ overflow: 'auto' }}>
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "column",
                        gap: "5px",
                        justifyContent: "center",
                        marginTop: "5px",
                        background: "#F5F6FA"
                    }}
                >
                    <Box sx={{ width: "90%", paddingTop: "10px" }}>
                        <Typography variant="h6">{ActiveUser?.companyName || ""}</Typography>
                        {sidebarAddressLine ? (
                        <Typography
                            variant="subtitle2"
                            sx={{
                                overflowWrap: "break-word",
                                wordWrap: "break-word",
                                wordBreak: "break-word"
                            }}
                        >
                            {sidebarAddressLine}
                        </Typography>
                        ) : null}
                        <Typography variant="caption" sx={{ color: "text.disabled" }}>
                            {getCompanyType(ActiveUser?.companyType)}
                        </Typography>
                    </Box>
                    <Divider sx={{ width: "95%" }} />
                    {DrawerMenuList.map((val) => (
                        <React.Fragment key={val.id}>
                            {"child" in val ? (
                                <DropDownMenuComponent
                                    selectedMenu={selectedMenu}
                                    navigateUser={navigateUser}
                                    data={val}
                                />
                            ) : (
                                <Box sx={{ width: "93%" }}>
                                    <MenuButton
                                        val={val}
                                        selectedMenu={selectedMenu}
                                        navigateUser={navigateUser}
                                    />
                                </Box>
                            )}
                        </React.Fragment>
                    ))}
                    <LogoutButton />
                </Box>
            </Box>
        </>
    );

    return (
        <>
            {isMobile && (
                <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={() => setMobileOpen(true)}
                    sx={{ position: 'fixed', top: 10, left: 10, zIndex: 2000 }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            <Drawer
                variant={isMobile ? 'temporary' : 'permanent'}
                open={isMobile ? mobileOpen : true}
                onClose={() => setMobileOpen(false)}
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        background: '#F5F6FA',
                    },
                }}
            >
                <HeaderBox switchToMainUser={switchToMainUser} />
                {drawerContent}
            </Drawer>
        </>
    );
}

export default MenuSidebar;
